/**
 * Inspector — one section's deep-dive, docked OVER the spine.
 *
 * Variant C's per-section surface. The spine shows progress; the inspector
 * shows depth. One section is focused at a time; tabs scope the view:
 *
 *   Draft        — the generated text
 *   Coverage     — subtask checklist (only while the review sidecar is on)
 *   Issues       — gap + regulatory ledger AND the regulatory-review run
 *                  control (the former "Review" tab was folded in here)
 *   Instructions — the instruction text served to the drafter
 *   History      — what the agents did (Action History)
 *   Refs         — published literature behind the run (EndNote export)
 *   Notes        — the drafting agents' workspace notes
 *
 * Header: back-to-spine control, prev/next section arrows, mono section
 * number + title, a status chip with a quiet meta line, and the section's
 * Generate / Load / Regen actions. Esc closes.
 *
 * # DECISION: the inspector is a full-height panel that slides in OVER the
 * #   spine (anchored to the spine's own box), not a 60vh drawer under it.
 * # WHY: at 360px a bottom drawer left ~3 spine rows and ~250px of tab body,
 * #      both cramped, and the writer could not tell which row it belonged
 * #      to (Guru, 2026-10-14: "people have no clue … I'm giving you
 * #      liberty"). Over-the-spine gives each tab the whole pane height, the
 * #      header says which section it is, prev/next steps through sections
 * #      without going back, and because the spine stays mounted underneath
 * #      the writer returns to the same scroll position on Back / Esc. A
 * #      right-to-left slide reads as "drilled in"; it is a 180ms nudge, off
 * #      under prefers-reduced-motion.
 * # DATE: 2026-10-14
 *
 * SRP: composes the tab shell + sub-tab components. Each tab owns its own
 * data fetch. The inspector itself is presentational; the host decides
 * which section is focused and positions the dock (a ``position: relative``
 * box around the spine — see VariantCShell).
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  ClipboardTextLtr16Regular,
  DocumentText16Regular,
  Flag16Regular,
  History16Regular,
  Notebook16Regular,
  TextBulletListSquare16Regular,
  TextQuote16Regular,
} from "@fluentui/react-icons";
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { BackLink, Button, FooterBar, IconButton, LinkButton, type PillStatus, StatusPill } from "../../ds/components";
import type { SpineSection } from "../hooks/useVariantCRun";
import { getEntry, reviewKey, subscribe as subscribeReview } from "../reviewStore";
import { formatGutterNumber } from "../sectionLabel";
import type { RunIdentity } from "../sseAdapter";
import { REVIEW_AGENT_ENABLED } from "../../config/reviewAgent";
import { useFlag } from "../../context/FeatureFlagsContext";
import { brandColors, fonts, inkColors, motion, statusColors, surfaceColors } from "../tokens";
import ActionHistoryTab from "./inspector/ActionHistoryTab";
import CoverageTabPanel from "./inspector/CoverageTabPanel";
import DataGapsTabPanel from "./inspector/DataGapsTabPanel";
import DraftTab from "./inspector/DraftTab";
import InstructionsTab from "./inspector/InstructionsTab";
import NotesTab from "./inspector/NotesTab";
import ReferencesTab from "./inspector/ReferencesTab";
import { formatDraftedSubline } from "./LoomSpine";

// ---------------------------------------------------------------------------
// Tab vocabulary
// ---------------------------------------------------------------------------

export type InspectorTab = "draft" | "coverage" | "gaps" | "instructions" | "actions" | "references" | "notes";

interface TabMeta {
  key: InspectorTab;
  /** Short label under the icon (fits seven tabs at 360px). */
  label: string;
  /** Full name — tooltip and accessible name. */
  fullLabel: string;
  icon: React.ReactElement;
}

const TABS: TabMeta[] = [
  { key: "draft", label: "Draft", fullLabel: "Draft", icon: <DocumentText16Regular /> },
  // Coverage is sidecar-backed (review_agent); hide it while the sidecar
  // kill switch is off so we don't poll the container. Flip
  // REVIEW_AGENT_ENABLED to restore the tab.
  ...(REVIEW_AGENT_ENABLED
    ? ([
        { key: "coverage", label: "Coverage", fullLabel: "Coverage", icon: <TextBulletListSquare16Regular /> },
      ] as TabMeta[])
    : []),
  // Issues (data gaps + regulatory flags + the regulatory-review run control)
  // sits right after the section output (Draft). Key stays "gaps" for back-compat.
  { key: "gaps", label: "Issues", fullLabel: "Issues", icon: <Flag16Regular /> },
  // Final instruction text served to the drafter, with conditional-block
  // activation verdicts (passed / excluded / unverified).
  { key: "instructions", label: "Instructions", fullLabel: "Instructions", icon: <ClipboardTextLtr16Regular /> },
  // What the agents did for this section (tool calls, reasoning, edits).
  { key: "actions", label: "Actions", fullLabel: "Action history", icon: <History16Regular /> },
  // Published literature behind the run, packaged for EndNote: .ris export +
  // insert-at-cursor of each reference's temporary-citation placeholder.
  { key: "references", label: "Refs", fullLabel: "References", icon: <TextQuote16Regular /> },
  // The drafting agents' workspace notes — document-scoped (folder-scoped when
  // the document is filed), so the same notes show under every section.
  { key: "notes", label: "Notes", fullLabel: "Notes", icon: <Notebook16Regular /> },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface InspectorProps {
  /** The section the inspector is scoped to. */
  section: SpineSection;
  /** Run identity for the data fetches in each tab. */
  runId: string;
  /** Doc-type code (e.g. "CSR"). Currently unused by the tabs. */
  docCode: string;
  /** (trial, session) captured from the run_metadata SSE event.
   *  Coverage + Review tabs gate their renders on this. */
  identity: RunIdentity | null;
  /** Close handler — host clears focusedSectionId. */
  onClose: () => void;
  /** Spec 23 P10.5 — optional "Regenerate this section" affordance
   *  in the header. Shell passes a handler only when regen is
   *  meaningful for the current state (typically post-run); omit
   *  to hide the button. Host opens the RegenOne modal + routes
   *  the confirmed intent. */
  onRegenerate?: () => void;
  /** Passed instead of onRegenerate when the section has never been
   *  drafted. Renders a "Generate" button that routes to the
   *  "Draft more sections" picker rather than the regen modal. */
  onGenerate?: () => void;
  /** Re-project this section's server draft into the live Word doc (header
   *  "Load" button). Passed only when the section has a draft; omit to hide. */
  onLoadIntoWord?: () => void;
  /** Select the section's heading in Word (footer "Show in Word"). */
  onShowInWord?: () => void;
  /** Stop this section's in-flight regenerate (footer "Stop section"). */
  onStopSection?: () => void;
  /** Copy a section or table word for word from a source document into this
   *  section (opens the copy picker). */
  onCopyFromSource?: () => void;
  /** (Re-)run the post-draft regulatory review for this section (header
   *  "Review" button). Passed only when the section has a draft; omit to hide.
   *  The button tracks the reviewStore entry itself (disabled while running)
   *  and switches to the Issues tab, where progress and findings show. */
  onRunReview?: () => void;
  /** True while this section's Load-into-Word write is in flight. */
  isLoadingIntoWord?: boolean;
  /** Monotonic signal that bumps each time the writer asks to open the
   *  Review tab for this section (e.g. clicking the spine's ``N⚖`` regulator
   *  badge). A change forces the drawer onto the Review tab — even when the
   *  section is already focused, where the section-change reset wouldn't
   *  fire. Ignored (stays on Draft) when undefined / 0. */
  openReviewSignal?: number;
  /** Monotonic signal that bumps each time a highlighted-span edit is approved
   *  for this section. A change re-fetches the Data Gaps tab's open-items list
   *  so it reflects the re-checked section. Ignored when undefined / 0. */
  issuesRefreshSignal?: number;
  /** Monotonic signal that bumps when this section's Action History may have
   *  changed (a lifecycle write, regen, resolve section, or run end). A change
   *  re-fetches the Action History tab. Ignored when undefined / 0. */
  actionsRefreshSignal?: number;
  /** True when another section is currently being regenerated. Used to
   *  disable Generate/Regenerate buttons while a regen is in progress
   *  to prevent concurrent regenerations. */
  isRegenInProgress?: boolean;
  /** Optional "Resolve with asthra" handler for the Data Gaps tab — seeds a
   *  chat turn that researches + resolves one gap marker. Hidden when
   *  absent (e.g. chat unavailable). */
  onResolveGapWithAsthra?: (issue: import("../../api/client").SectionIssue) => void;
  /** Optional bulk "Resolve selected" handler for the Issues tab — opens the
   *  resolve wizard for the checked issues. Selection UI hidden when absent. */
  onResolveSelectedGaps?: (targets: import("./SubmissionIssuesTab").ResolveSelectionTarget[]) => void;
  /** One-click fix of these flags (no window) — rows' "Fix" and the bulk button. */
  onFixGapsNow?: (targets: import("./SubmissionIssuesTab").ResolveSelectionTarget[]) => void;
  /** Live per-section agent step lines for the focused section (from the shell's
   *  agentActivityFeed). Drives the Issues tab's expandable "full step feed"
   *  while a regulatory review runs. Empty when no feed is active. */
  auditActivityLines?: readonly import("../services/agentActivityFeed").AgentActivityLine[];
  /** True when the current run is a custom submission (built on the writer's own
   *  Word document). Turns the Instructions tab into a per-section instruction
   *  EDITOR (custom sections have no template instruction to display). */
  isCustomSubmission?: boolean;
  /** Called after a custom section's instruction is saved/cleared so the shell
   *  can update the spine row that gates the Generate / Draft-more buttons. */
  onInstructionSaved?: (sectionId: string, instruction: string | null) => void;
  /** The spine's sections in display order. With ``onNavigate`` it powers
   *  the header's previous / next section arrows. */
  sections?: readonly SpineSection[];
  /** Focus another section without leaving the inspector (prev / next).
   *  The current tab is kept, so a writer can step through every section's
   *  Action History in a row. */
  onNavigate?: (sectionId: string) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const actionBtn = {
  fontSize: "9.5px",
  fontFamily: fonts.mono,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: brandColors.primary,
  background: "var(--surface-raised)",
  border: `1px solid color-mix(in srgb, ${brandColors.mid} 33%, transparent)`,
  borderRadius: "4px",
  height: "22px",
  padding: "0 8px",
  cursor: "pointer",
  flexShrink: 0,
  whiteSpace: "nowrap",
} as const;

const iconBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "26px",
  height: "26px",
  padding: 0,
  borderRadius: "6px",
  background: "transparent",
  border: "none",
  color: inkColors.ink3,
  cursor: "pointer",
  flexShrink: 0,
} as const;

const useStyles = makeStyles({
  root: {
    position: "absolute",
    inset: "0px",
    zIndex: 20,
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    background: "var(--surface)",
    color: "var(--ink)",
    fontFamily: "var(--font-sans)",
    animationName: {
      from: { transform: "translateX(18px)", opacity: 0 },
      to: { transform: "translateX(0)", opacity: 1 },
    },
    animationDuration: "180ms",
    animationTimingFunction: "cubic-bezier(0.16, 1, 0.3, 1)",
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
    },
  },
  header: {
    flexShrink: 0,
    padding: "6px 12px 10px",
    background: surfaceColors.paper,
    borderBottom: `1px solid ${surfaceColors.line}`,
    minWidth: 0,
  },
  navRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    marginLeft: "-6px",
    marginRight: "-4px",
  },
  backBtn: {
    ...iconBtn,
    width: "auto",
    gap: "6px",
    padding: "0 8px 0 6px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: inkColors.ink3,
    ":hover": { background: brandColors.light, color: brandColors.primary },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-2px" },
  },
  navBtn: {
    ...iconBtn,
    ":hover": { background: brandColors.light, color: brandColors.primary },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "-2px" },
    ":disabled": { color: inkColors.ink5, cursor: "default", background: "transparent" },
  },
  position: {
    marginLeft: "auto",
    marginRight: "2px",
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  titleRow: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    marginTop: "4px",
    minWidth: 0,
  },
  numberGutter: {
    flexShrink: 0,
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
  },
  title: {
    flex: 1,
    minWidth: 0,
    fontSize: "15px",
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: "-0.1px",
    color: inkColors.ink,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  metaRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "6px",
    minWidth: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    flexShrink: 0,
    height: "16px",
    padding: "0 5px",
    borderRadius: "4px",
    border: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.mono,
    fontSize: "9px",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    lineHeight: 1,
    color: inkColors.ink4,
    whiteSpace: "nowrap",
  },
  chipLive: {
    color: brandColors.primary,
    background: brandColors.light,
    border: `1px solid color-mix(in srgb, ${brandColors.mid} 33%, transparent)`,
  },
  chipDone: {
    color: inkColors.ink3,
    background: `color-mix(in srgb, ${statusColors.cool} 10%, transparent)`,
    border: `1px solid color-mix(in srgb, ${statusColors.cool} 40%, transparent)`,
  },
  chipApproval: {
    color: inkColors.ink2,
    background: `color-mix(in srgb, ${statusColors.warn} 10%, transparent)`,
    border: `1px solid color-mix(in srgb, ${statusColors.warn} 50%, transparent)`,
  },
  chipDanger: {
    color: statusColors.danger,
    background: `color-mix(in srgb, ${statusColors.danger} 6%, transparent)`,
    border: `1px solid color-mix(in srgb, ${statusColors.danger} 30%, transparent)`,
  },
  metaText: {
    flex: 1,
    minWidth: 0,
    fontFamily: fonts.mono,
    fontSize: "10px",
    color: inkColors.ink4,
    fontVariantNumeric: "tabular-nums",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actions: {
    display: "flex",
    gap: "4px",
    flexShrink: 0,
    marginLeft: "auto",
  },
  // Header actions (Generate / Load / Regen): compact mono caps, violet hairline.
  regenBtn: {
    ...actionBtn,
    ":hover": { background: brandColors.light, border: `1px solid ${brandColors.mid}` },
    ":focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  // -- tab strip -----------------------------------------------------------
  tabsWrap: {
    position: "relative",
    flexShrink: 0,
    borderBottom: `1px solid ${surfaceColors.line}`,
    background: "var(--surface-raised)",
  },
  tabs: {
    display: "flex",
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    padding: "0 6px",
    scrollbarWidth: "none",
    "::-webkit-scrollbar": { display: "none" },
  },
  // Right-edge fade while more tabs sit off-screen (7 tabs at 360px).
  fade: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "28px",
    pointerEvents: "none",
    background: "linear-gradient(to right, transparent, var(--surface-raised) 80%)",
  },
  tab: {
    position: "relative",
    flex: "1 0 auto",
    minWidth: "44px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "3px",
    padding: "8px 5px 7px",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    color: inkColors.ink4,
    whiteSpace: "nowrap",
    ":hover": { color: inkColors.ink2 },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "-2px",
      borderRadius: "2px",
    },
  },
  tabActive: {
    color: inkColors.ink,
    borderBottom: `2px solid ${brandColors.primary}`,
  },
  tabIcon: {
    display: "inline-flex",
    position: "relative",
    "& svg": { width: "16px", height: "16px" },
  },
  tabIconActive: {
    color: brandColors.primary,
  },
  tabLabel: {
    fontFamily: fonts.mono,
    fontSize: "9.5px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    lineHeight: 1,
  },
  // Launch-film counter badge (TabBar's counter), pinned to the icon corner.
  counter: {
    position: "absolute",
    top: "-5px",
    left: "11px",
    minWidth: "14px",
    height: "13px",
    padding: "0 3px",
    borderRadius: "5px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: fonts.mono,
    fontSize: "9px",
    fontWeight: 600,
    fontStyle: "italic",
    color: brandColors.primary,
    background: brandColors.light,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1,
  },
  body: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    overflowX: "hidden",
    overflowY: "auto",
    transitionProperty: "opacity",
    transitionDuration: `${motion.transitionMs}ms`,
  },
});

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/** Header status chip — the row's state in words (matches the spine chip). */
function statusChip(section: SpineSection): { status: PillStatus; label: string } {
  if (section.failed && section.status === "flagged") return { status: "failed", label: "Failed" };
  if (section.auditPhase === "running") return { status: "review", label: "Reviewing" };
  if (section.postDraftTask === "review") return { status: "review", label: "Reviewing" };
  if (section.postDraftTask === "fill") return { status: "review", label: "Filling flags" };
  switch (section.status) {
    case "drafting":
      return { status: "drafting", label: "Drafting" };
    case "drafted":
      return { status: "done", label: "Done" };
    case "needs_approval":
      return { status: "paused", label: "Needs approval" };
    case "flagged":
      return { status: "failed", label: "Flagged" };
    case "skipped":
      return { status: "queued", label: "Skipped" };
    default:
      return { status: "queued", label: "Queued" };
  }
}

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}

const isEditable = (el: EventTarget | null): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT";
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Inspector: React.FC<InspectorProps> = ({
  section,
  runId,
  identity,
  onClose,
  onRegenerate,
  onGenerate,
  onLoadIntoWord,
  onShowInWord,
  onStopSection,
  onCopyFromSource,
  onRunReview,
  isLoadingIntoWord,
  openReviewSignal,
  issuesRefreshSignal,
  actionsRefreshSignal,
  isRegenInProgress,
  onResolveGapWithAsthra,
  onResolveSelectedGaps,
  onFixGapsNow,
  auditActivityLines,
  isCustomSubmission,
  onInstructionSaved,
  sections,
  onNavigate,
}) => {
  const styles = useStyles();
  const [tab, setTab] = useState<InspectorTab>("draft");
  const flagEnabled = useFlag("@test.feature-flag");
  // Prev / next keep the writer's tab; any other focus change lands on Draft.
  const keepTabRef = useRef(false);
  // Resetting to "draft" whenever the focused section changes keeps
  // navigation predictable — clicking around the spine always lands
  // on the writer's primary surface, not whatever tab they last opened.
  const sectionKey = section.id;
  React.useEffect(() => {
    void sectionKey; // trigger: reset tab when section changes
    if (keepTabRef.current) {
      keepTabRef.current = false;
      return;
    }
    setTab("draft" as InspectorTab);
  }, [sectionKey]);

  // Spine ``N⚖`` regulator-badge clicks bump openReviewSignal to force the
  // Issues tab open (the regulatory review now lives there). Declared AFTER the
  // section-reset effect on purpose: when a badge click changes the focused
  // section AND bumps the signal in the same commit, React runs effects in
  // declaration order — the reset writes "draft" first, then this writes
  // "gaps", so the Issues tab wins.
  React.useEffect(() => {
    if (!openReviewSignal) return; // undefined/0 → stay on Draft (prop contract)
    setTab("gaps" as InspectorTab);
  }, [openReviewSignal]);

  // Header "Review" button state — same store entry the Issues tab reads, so
  // both buttons agree on running / has-run.
  const reviewStoreKey = reviewKey(runId, section.id);
  const reviewEntry = useSyncExternalStore(
    useCallback((cb: () => void) => subscribeReview(reviewStoreKey, cb), [reviewStoreKey]),
    () => getEntry(reviewStoreKey),
  );
  const reviewRunning = reviewEntry.runState === "running" || section.auditPhase === "running";
  const reviewHasRun = reviewEntry.hasRun || reviewEntry.flagCount > 0 || section.auditPhase === "done";
  const runReview = useCallback(() => {
    if (!onRunReview) return;
    setTab("gaps" as InspectorTab);
    onRunReview();
  }, [onRunReview]);

  // Draft and Issues tabs always visible; filter other tabs based on feature flag
  const visibleTabs = React.useMemo(() => {
    if (flagEnabled) {
      return TABS.filter(
        (t) =>
          t.key === "draft" ||
          t.key === "gaps" ||
          t.key === "instructions" ||
          t.key === "actions" ||
          t.key === "references" ||
          t.key === "notes",
      );
    }
    return TABS;
  }, [flagEnabled]);

  // Esc returns to the spine — unless the writer is typing in a field (the
  // Instructions editor), where Esc belongs to the field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || isEditable(e.target)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prev / next section.
  const idx = sections ? sections.findIndex((s) => s.id === section.id) : -1;
  const prev = idx > 0 && sections ? sections[idx - 1] : null;
  const next = idx >= 0 && sections && idx < sections.length - 1 ? sections[idx + 1] : null;
  const go = useCallback(
    (target: SpineSection | null) => {
      if (!target || !onNavigate) return;
      keepTabRef.current = true;
      onNavigate(target.id);
    },
    [onNavigate],
  );

  // Live elapsed for a running section (1s tick only while it runs).
  const running =
    section.status === "drafting" || section.auditPhase === "running" || section.postDraftTask !== undefined;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);

  // Tab strip: fade the right edge while tabs overflow and aren't scrolled to the end.
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const [fadeRight, setFadeRight] = useState(false);
  const measureTabs = useCallback(() => {
    const el = tabsRef.current;
    if (!el) return;
    setFadeRight(el.scrollWidth - el.clientWidth - el.scrollLeft > 2);
  }, []);
  useEffect(() => {
    measureTabs();
    const el = tabsRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measureTabs);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureTabs]);
  // Keep the active tab in view when it changes (e.g. Issues forced open).
  useEffect(() => {
    const btn = tabsRef.current?.querySelector<HTMLElement>(`[data-tab-key="${tab}"]`);
    btn?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [tab]);

  const onTabsKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const i = visibleTabs.findIndex((t) => t.key === tab);
    const n = visibleTabs[(i + (e.key === "ArrowRight" ? 1 : -1) + visibleTabs.length) % visibleTabs.length];
    setTab(n.key);
    tabsRef.current?.querySelector<HTMLElement>(`[data-tab-key="${n.key}"]`)?.focus();
  };

  const issueCount = (section.contentGapCount ?? 0) + (section.regulatorFlagCount ?? 0);

  let body: React.ReactNode;
  switch (tab) {
    case "draft":
      body = <DraftTab section={section} runId={runId} activity={auditActivityLines} />;
      break;
    case "coverage":
      body = <CoverageTabPanel identity={identity} moduleId={section.id} />;
      break;
    case "gaps":
      body = (
        <DataGapsTabPanel
          section={section}
          runId={runId}
          onResolveWithAsthra={onResolveGapWithAsthra}
          onResolveSelected={onResolveSelectedGaps}
          onFixNow={onFixGapsNow}
          issuesRefreshSignal={issuesRefreshSignal}
          identity={identity}
          auditActivityLines={auditActivityLines}
        />
      );
      break;
    case "instructions":
      body = (
        <InstructionsTab
          section={section}
          runId={runId}
          isCustomSubmission={isCustomSubmission}
          onInstructionSaved={onInstructionSaved}
        />
      );
      break;
    case "actions":
      // WHY: Guru's feedback (2026-10-14) — the reviewer's History row opens
      // the Issues tab. DATE: 2026-10-14
      body = (
        <ActionHistoryTab
          section={section}
          runId={runId}
          refreshSignal={actionsRefreshSignal}
          onOpenIssues={() => setTab("gaps" as InspectorTab)}
        />
      );
      break;
    case "references":
      body = <ReferencesTab section={section} runId={runId} />;
      break;
    case "notes":
      body = <NotesTab />;
      break;
  }

  const chip = statusChip(section);
  const meta = running
    ? [section.subline, section.sublineStartedAt ? fmtElapsed(now - section.sublineStartedAt) : null]
        .filter(Boolean)
        .join(" · ")
    : section.failed && section.failureReason
      ? section.failureReason
      : section.status === "drafted"
        ? formatDraftedSubline(section)
        : null;
  const gutter = formatGutterNumber(section.number);

  // Design boards 12/13: back to Spine, "2 of 3" with up/down, number +
  // title, status pill + meta, tabs, body, and a two-button footer.
  return (
    <section
      className={mergeClasses("as-insp", styles.root)}
      aria-label={`Section ${gutter ? `${gutter} ` : ""}${section.title}`}
      data-testid="inspector"
    >
      <div className="as-insp-head">
        <div className="as-stephead-top">
          <BackLink onClick={onClose} aria-label="Close inspector" title="Back to the spine (Esc)">
            Spine
          </BackLink>
          {sections && onNavigate && idx >= 0 && (
            <span className="as-insp-nav">
              <span className="as-insp-pos">
                {idx + 1} of {sections.length}
              </span>
              <IconButton
                icon="up"
                label="Previous section"
                outline
                onClick={() => go(prev)}
                disabled={!prev}
                title={prev ? `Previous: ${prev.number} ${prev.title}` : "First section"}
              />
              <IconButton
                icon="down"
                label="Next section"
                outline
                onClick={() => go(next)}
                disabled={!next}
                title={next ? `Next: ${next.number} ${next.title}` : "Last section"}
              />
            </span>
          )}
        </div>
        <div className="as-insp-title">
          {gutter ? <span className="as-insp-num">{gutter}</span> : <span />}
          <span>
            <h1 className="as-insp-h1" title={section.title}>
              {section.title}
            </h1>
            <span className="as-srow-sub">
              <StatusPill status={chip.status}>{chip.label}</StatusPill>
              {meta ? (
                <span className="as-srow-step" title={meta}>
                  {meta}
                </span>
              ) : null}
              {onLoadIntoWord && (
                <LinkButton
                  onClick={onLoadIntoWord}
                  disabled={isLoadingIntoWord}
                  aria-label={`Load section ${section.number} into Word`}
                  title="Write this section's current draft into the Word document"
                >
                  {isLoadingIntoWord ? "Loading…" : "Load into Word"}
                </LinkButton>
              )}
              {onCopyFromSource && (
                <LinkButton
                  onClick={onCopyFromSource}
                  aria-label={`Copy into section ${section.number} from a source`}
                  title="Copy a section or table word for word from a source document"
                >
                  Insert from source
                </LinkButton>
              )}
              {onRunReview && (
                <LinkButton
                  onClick={runReview}
                  disabled={reviewRunning}
                  aria-label={`${reviewHasRun ? "Re-run" : "Run"} the regulatory review for section ${section.number}`}
                  title={
                    reviewRunning
                      ? "A regulatory review is running for this section"
                      : reviewHasRun
                        ? "Re-run the regulatory review on this section's current draft"
                        : "Run the regulatory review on this section's draft"
                  }
                >
                  {reviewRunning ? "Reviewing…" : reviewHasRun ? "Re-review" : "Review"}
                </LinkButton>
              )}
            </span>
          </span>
        </div>
      </div>

      <div
        ref={tabsRef}
        className="as-tabs as-insp-tabs"
        role="tablist"
        aria-label="Section details"
        onScroll={measureTabs}
        onKeyDown={onTabsKeyDown}
      >
        {visibleTabs.map((t) => {
          const active = tab === t.key;
          const count = t.key === "gaps" && issueCount > 0 ? issueCount : null;
          return (
            <button
              type="button"
              key={t.key}
              role="tab"
              className="as-tab"
              data-tab-key={t.key}
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onClick={() => setTab(t.key)}
              title={t.fullLabel}
              aria-label={count ? `${t.fullLabel}, ${count} open` : t.fullLabel}
            >
              {t.label}
              {count ? <span className="as-count as-count-alert">{count}</span> : null}
            </button>
          );
        })}
        {fadeRight && <span className={styles.fade} aria-hidden="true" />}
      </div>

      <div className={mergeClasses("as-insp-body", styles.body)} role="tabpanel">
        {body}
      </div>

      {(onRegenerate || onGenerate || onShowInWord || onStopSection) && (
        <FooterBar note={running ? "It loads into Word at its heading as soon as drafting finishes." : undefined}>
          {onStopSection && running ? (
            <Button variant="secondary" onClick={onStopSection}>
              Stop section
            </Button>
          ) : onGenerate ? (
            <Button
              variant="secondary"
              onClick={onGenerate}
              disabled={isRegenInProgress}
              aria-label={`Generate section ${section.number}`}
              title={isRegenInProgress ? "Wait for the current section regeneration to complete" : "Draft this section"}
            >
              Draft this section
            </Button>
          ) : onRegenerate ? (
            <Button
              variant="secondary"
              onClick={onRegenerate}
              disabled={isRegenInProgress}
              aria-label={`Regenerate section ${section.number}`}
              title={
                isRegenInProgress
                  ? "Wait for the current section regeneration to complete"
                  : "Re-draft this section from scratch"
              }
            >
              Regenerate
            </Button>
          ) : (
            <span />
          )}
          <Button
            variant="primary"
            onClick={onShowInWord}
            disabled={!onShowInWord || running || section.status === "queued"}
            title="Select this section's heading in Word"
          >
            Show in Word
          </Button>
        </FooterBar>
      )}
    </section>
  );
};

export default Inspector;
